import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import SEO from "../components/SEO";

function formatTimeRemaining(endTime: string) {
  const diff = new Date(endTime).getTime() - Date.now();
  if (diff <= 0) return "Ended";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function formatAmount(value: string | number) {
  return `$${value}`;
}

export default function Auctions() {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({ activeCount: 0, endingSoonCount: 0, watchlistCount: 0 });
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [auctionsEnabled, setAuctionsEnabled] = useState(true);
  const [watchedIds, setWatchedIds] = useState<Set<number>>(new Set());
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setLoading(true);
    const statusParam = tab === "upcoming" ? "upcoming" : tab === "ended" ? "ended" : "active";
    fetch(`/api/auctions?status=${statusParam}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setAuctions(data.auctions || []);
        setStats(data.stats || { activeCount: 0, endingSoonCount: 0, watchlistCount: 0 });
        setAuctionsEnabled(data.auctionsEnabled !== false);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    if (!user || !auctionsEnabled) return;
    fetch("/api/auctions/watchlist", { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setWatchedIds(new Set(data.map((a: any) => a.id)));
        }
      })
      .catch(() => {});
  }, [user, auctionsEnabled]);

  const toggleWatch = async (auctionId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    const res = await fetch(`/api/auctions/${auctionId}/watch`, { method: "POST", credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      setWatchedIds(prev => {
        const next = new Set(prev);
        if (data.watching) next.add(auctionId);
        else next.delete(auctionId);
        return next;
      });
      setStats((s: any) => ({ ...s, watchlistCount: data.watching ? s.watchlistCount + 1 : Math.max(0, s.watchlistCount - 1) }));
    }
  };

  const tabs = [
    { key: "active", label: "Active" },
    { key: "upcoming", label: "Upcoming" },
    { key: "ended", label: "Ended" },
  ];

  return (
    <>
      <SEO
        title="Daily Pharma Deals — Short-Dated & Overstock"
        description="Daily pharma auctions — short-dated & overstock generics at up to 70% off. Licensed pharmacies, clinics & veterinary practices only. New deals every day."
        path="/auctions"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Auctions", path: "/auctions" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Daily Pharma Deals — Short-Dated & Overstock",
          description: "Daily pharma auctions — short-dated & overstock generics at up to 70% off. Licensed pharmacies, clinics & veterinary practices only. New deals every day.",
          url: "https://premierpharma.com/auctions",
          isPartOf: { "@id": "https://premierpharma.com/#website" },
        }}
      />
      <section className="py-8 bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-primary">Daily Pharma Deals</h1>
                <span className="bg-accent/10 text-accent text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Daily Deals</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">New deals every day — staggered windows ending before 4 PM</p>
            </div>
            <Link to="/products" className="text-accent text-sm hover:underline">← Back to Catalog</Link>
          </div>

          <div className="bg-gradient-to-r from-accent via-blue-600 to-indigo-600 rounded-xl p-5 mb-6 text-white">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <div className="font-semibold">Today's Daily Deals</div>
                  <div className="text-sm text-white/80">Staggered throughout the day — free shipping included</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-1.5 text-sm font-medium">
                <svg className="w-4 h-4 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                Free Shipping
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card text-center">
              <div className="text-2xl font-bold text-accent">{stats.activeCount}</div>
              <div className="text-xs text-gray-500">Active Deals</div>
            </div>
            <div className="card text-center">
              <div className="text-2xl font-bold text-orange-500">{stats.endingSoonCount}</div>
              <div className="text-xs text-gray-500">Ending Soon</div>
            </div>
            <div className="card text-center">
              <div className="text-2xl font-bold text-green-600">Up to 70%</div>
              <div className="text-xs text-gray-500">Total Savings</div>
            </div>
            <div className="card text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.watchlistCount}</div>
              <div className="text-xs text-gray-500">Your Watchlist</div>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                aria-pressed={tab === t.key}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "bg-accent text-white" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading auctions...</div>
          ) : !auctionsEnabled ? (
            <div className="card text-center py-16">
              <div className="text-5xl mb-4">🔒</div>
              <h3 className="text-lg font-semibold text-primary mb-2">Auctions Not Currently Available</h3>
              <p className="text-gray-500">Daily deals are temporarily unavailable. Please check back soon.</p>
            </div>
          ) : auctions.length === 0 ? (
            <div className="card text-center py-12">
              <div className="text-5xl mb-4">🔨</div>
              <h3 className="text-lg font-semibold text-primary mb-2">No {tab} auctions</h3>
              <p className="text-gray-500">Check back later for new deals!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {auctions.map((a: any) => {
                const timeLeft = formatTimeRemaining(a.endTime);
                const msRemaining = new Date(a.endTime).getTime() - Date.now();
                const isEndingUrgent = msRemaining > 0 && msRemaining < 1 * 60 * 60 * 1000 && a.status === "active";
                const isEndingSoon = msRemaining > 0 && msRemaining < 4 * 60 * 60 * 1000 && a.status === "active";
                return (
                  <Link key={a.id} to={`/auctions/${a.id}`} className={`card hover:shadow-lg transition-shadow relative ${isEndingUrgent ? "ring-2 ring-red-400 ring-opacity-50" : ""}`}>
                    <div className="absolute top-3 right-3 flex items-center gap-1.5">
                      {a.status === "active" && (
                        <span className="bg-accent/10 text-accent text-xs font-bold px-2 py-0.5 rounded-full">Daily Deal</span>
                      )}
                      {a.freeShipping && (
                        <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">Free Shipping</span>
                      )}
                    </div>
                    {user && (
                      <button
                        onClick={(e) => toggleWatch(a.id, e)}
                        aria-label={watchedIds.has(a.id) ? "Remove from watchlist" : "Add to watchlist"}
                        aria-pressed={watchedIds.has(a.id)}
                        className="absolute top-3 left-3 text-xl hover:scale-110 transition-transform"
                      >
                        <span aria-hidden="true">{watchedIds.has(a.id) ? "❤️" : "🤍"}</span>
                      </button>
                    )}
                    <div className="mt-4">
                      <div className="flex items-start gap-2 mb-1">
                        <h3 className="font-semibold text-primary text-sm leading-tight">{a.productName || a.title}</h3>
                        {a.itemCount > 1 && (
                          <span className="inline-flex items-center shrink-0 bg-accent/10 text-accent text-xs font-medium px-2 py-0.5 rounded-full">
                            {a.itemCount} items
                          </span>
                        )}
                      </div>
                      {a.itemCount > 1 ? (
                        <div className="text-xs text-gray-500 mb-3">
                          <span className="text-accent font-medium">Bundle</span>
                          <span> · {a.productName || a.title} & {a.itemCount - 1} more item{a.itemCount - 1 !== 1 ? "s" : ""}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 mb-3">
                          <span>NDC: {a.productNdc}</span>
                          {a.productStrength && <span> · {a.productStrength}</span>}
                          {a.productPackageSize && <span> · {a.productPackageSize}</span>}
                        </div>
                      )}

                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-xl font-bold text-accent">
                          {formatAmount(a.currentHighBid || a.startingPrice)}
                        </span>
                        {a.currentHighBid && parseFloat(a.currentHighBid) !== parseFloat(a.startingPrice) && (
                          <span className="text-sm text-gray-400 line-through">{formatAmount(a.startingPrice)}</span>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span>{a.bidCount} bid{a.bidCount !== 1 ? "s" : ""}</span>
                        <span className={`font-medium flex items-center gap-1 ${isEndingUrgent ? "text-red-600 animate-pulse" : isEndingSoon ? "text-orange-500" : a.status === "ended" ? "text-gray-400" : "text-gray-600"}`}>
                          {isEndingUrgent && <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>}
                          {a.status === "ended" ? "Ended" : a.status === "scheduled" ? `Starts ${new Date(a.startTime).toLocaleDateString()}` : timeLeft}
                        </span>
                      </div>

                      {a.status === "active" && (
                        <div className="w-full bg-accent text-white text-center py-2 rounded-lg text-sm font-medium">
                          Place Bid
                        </div>
                      )}
                      {a.status === "ended" && a.winningBid && (
                        <div className="w-full bg-gray-100 text-gray-600 text-center py-2 rounded-lg text-sm font-medium">
                          Won at {formatAmount(a.winningBid)}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
